import { getAuth } from 'firebase/auth'
import { saveAs } from 'file-saver'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kArtifactDownloadDocxApiPath } from '@/constants/constants-api-paths'
import { artifactNameFallback } from '@/constants/constants-artifact'
import { getFileNameFromContentDisposition } from '@/util/api/content-disposition'

/**
 * Fetches an artifact version as a DOCX blob for saving (without triggering download)
 * @returns Promise containing the blob and filename
 */
export async function fetchArtifactDocxBlobToSave(artifactVersionId: string): Promise<{ blob: Blob }> {
  const token = await getAuth().currentUser?.getIdToken(true)
  if (!token) throw new Error(kErrorCodeAuthError)

  // Create transaction id
  const sentry_transaction_id = nanoid()

  try {
    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kArtifactDownloadDocxApiPath.replace('{artifactVersionId}', artifactVersionId)}`

    const requestOptions: RequestInit = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
    }

    const response = await fetch(apiUrl, requestOptions)
    if (!response.ok) {
      throw new Error(`Failed to fetch artifact docx. Status: ${response.status}`)
    }

    // Convert the response to a Blob
    const blob = await response.blob()

    return { blob }
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentry_transaction_id })

      Sentry.captureException(error, {
        extra: {
          onLine: navigator.onLine,
          cookieEnabled: navigator.cookieEnabled,
          artifactVersionId,
          error: JSON.stringify(error),
        },
      })
    })
    throw error
  }
}

/**
 * Downloads an artifact version as a DOCX file
 */
export async function downloadArtifactAsDocX(artifactVersionId: string) {
  const token = await getAuth().currentUser?.getIdToken(true)
  if (!token) throw new Error(kErrorCodeAuthError)

  // Create transaction id
  const sentry_transaction_id = nanoid()

  try {
    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kArtifactDownloadDocxApiPath.replace('{artifactVersionId}', artifactVersionId)}`

    const requestOptions: RequestInit = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
    }

    const response = await fetch(apiUrl, requestOptions)
    if (!response.ok) {
      throw new Error(`Failed to download docx. Status: ${response.status}`)
    }

    const filename = getFileNameFromContentDisposition(response.headers.get('Content-Disposition')) ?? `${artifactNameFallback}.docx`

    // Convert the response to a Blob
    const blob = await response.blob()

    // Save the Blob as a file
    saveAs(blob, filename)
    return filename
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentry_transaction_id })

      Sentry.captureException(error, {
        extra: {
          onLine: navigator.onLine,
          cookieEnabled: navigator.cookieEnabled,
          artifactVersionId,
          error: JSON.stringify(error),
        },
      })
    })
    throw error
  }
}
